import { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Timestamp } from 'firebase/firestore';
import type { AmendMade, AmendsEntry, AmendsType, Fellowship, StepNumber } from '../../../types/stepWork';
import {
  getAmendsMade,
  addAmendMade,
  deleteAmendMade,
  getAmendsEntries,
} from '../../../services/firebase/stepWork';

interface Step9TabProps {
  userId: string;
  fellowship: Fellowship;
  stepNumber: StepNumber;
  disabled?: boolean;
}

const AMEND_TYPE_LABELS: Record<AmendsType, string> = {
  direct: 'Direct (in person)',
  indirect: 'Indirect (financial/written)',
  living: 'Living Amend (changed behavior)',
  written: 'Written (letter)',
};

const AMEND_TYPE_DESCRIPTIONS: Record<AmendsType, string> = {
  direct: 'Face-to-face conversation',
  indirect: 'Financial restitution or letter when direct contact not possible',
  living: 'Changed behavior over time, e.g., being a better parent or employee',
  written: 'Letter sent when in-person would cause harm to them or others',
};

const emptyForm = {
  personName: '',
  amendsType: 'direct' as AmendsType,
  dateOfAmend: new Date().toISOString().split('T')[0],
  whatWasSaid: '',
  outcome: '',
  notes: '',
  step8EntryId: undefined as string | undefined,
};

export function Step9Tab({ userId, fellowship, disabled = false }: Step9TabProps) {
  const [amendsMade, setAmendsMade] = useState<AmendMade[]>([]);
  const [step8Entries, setStep8Entries] = useState<AmendsEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [userId, fellowship]);

  async function loadData() {
    try {
      setLoading(true);
      const [made, step8] = await Promise.all([
        getAmendsMade(userId, fellowship),
        getAmendsEntries(userId, fellowship),
      ]);
      setAmendsMade(made);
      setStep8Entries(step8);
    } catch (error) {
      console.error('Error loading amends data:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleStep8Selection(entryId: string) {
    const entry = step8Entries.find(e => e.id === entryId);
    if (entry) {
      setForm(prev => ({
        ...prev,
        personName: entry.personName,
        step8EntryId: entryId,
      }));
    } else {
      setForm(prev => ({ ...prev, personName: '', step8EntryId: undefined }));
    }
  }

  async function handleAdd() {
    if (!form.personName.trim() || !form.whatWasSaid.trim()) return;

    setSaving(true);
    try {
      await addAmendMade(userId, fellowship, {
        fellowship,
        personName: form.personName,
        amendsType: form.amendsType,
        dateOfAmend: Timestamp.fromDate(new Date(form.dateOfAmend)),
        whatWasSaid: form.whatWasSaid,
        outcome: form.outcome,
        notes: form.notes,
        step8EntryId: form.step8EntryId,
      });
      setForm(emptyForm);
      setShowAddForm(false);
      await loadData();
    } catch (error) {
      console.error('Error adding amend:', error);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(amendId: string) {
    if (!confirm('Remove this amend record?')) return;
    try {
      await deleteAmendMade(userId, amendId);
      setAmendsMade(prev => prev.filter(a => a.id !== amendId));
    } catch (error) {
      console.error('Error deleting amend:', error);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-slate-600 dark:text-slate-400">Loading amends records...</div>
      </div>
    );
  }

  const step8Total = step8Entries.length;
  const step8WithAmend = new Set(
    amendsMade.map(a => a.step8EntryId).filter(Boolean)
  ).size;

  // Which Step 8 entries still have no amend made
  const step8EntryIds = new Set(amendsMade.map(a => a.step8EntryId).filter(Boolean));
  const pendingStep8 = step8Entries.filter(e => !step8EntryIds.has(e.id));

  return (
    <div className="space-y-8">
      {/* Introduction */}
      <div className="p-4 bg-success-50 dark:bg-success-900/10 rounded-lg border border-success-200 dark:border-success-800">
        <h3 className="font-semibold text-navy-900 dark:text-navy-100 mb-2">
          Step 9: Made Direct Amends
        </h3>
        <p className="text-sm text-slate-700 dark:text-slate-300 italic">
          "Made direct amends to such people wherever possible, except when to do so would injure them or others."
        </p>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
          Record each amend as you make it. Track what was said, how it went, and the outcome.
          This is the action step that transforms your Step 8 list into real change.
        </p>
      </div>

      {/* Progress Summary */}
      {step8Total > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-white dark:bg-navy-800 rounded-lg border border-slate-200 dark:border-navy-700">
            <p className="text-sm text-slate-600 dark:text-slate-400">Step 8 List</p>
            <p className="text-2xl font-bold text-navy-900 dark:text-navy-100">{step8Total}</p>
          </div>
          <div className="p-4 bg-white dark:bg-navy-800 rounded-lg border border-slate-200 dark:border-navy-700">
            <p className="text-sm text-slate-600 dark:text-slate-400">Amends Made</p>
            <p className="text-2xl font-bold text-success-600 dark:text-success-400">{amendsMade.length}</p>
          </div>
          <div className="p-4 bg-white dark:bg-navy-800 rounded-lg border border-slate-200 dark:border-navy-700">
            <p className="text-sm text-slate-600 dark:text-slate-400">Step 8 Covered</p>
            <p className="text-2xl font-bold text-teal-600 dark:text-teal-400">
              {step8Total > 0 ? Math.round((step8WithAmend / step8Total) * 100) : 0}%
            </p>
          </div>
        </div>
      )}

      {/* Pending from Step 8 */}
      {pendingStep8.length > 0 && (
        <div className="p-4 bg-warning-50 dark:bg-warning-900/10 rounded-lg border border-warning-200 dark:border-warning-800">
          <h4 className="font-semibold text-navy-900 dark:text-navy-100 mb-2 text-sm">
            Still Pending from Step 8 ({pendingStep8.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {pendingStep8.map(e => (
              <span
                key={e.id}
                className="text-xs px-2 py-1 rounded-full bg-warning-100 dark:bg-warning-900/30 text-warning-800 dark:text-warning-300"
              >
                {e.personName}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Add Amend Button */}
      {!disabled && (
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-semibold transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Record an Amend
        </button>
      )}

      {/* Add Form */}
      {showAddForm && (
        <div className="p-6 bg-white dark:bg-navy-800 rounded-xl border border-slate-200 dark:border-navy-700 space-y-4">
          <h4 className="font-semibold text-navy-900 dark:text-navy-100">Record an Amend</h4>

          {/* Link to Step 8 */}
          {step8Entries.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-navy-800 dark:text-navy-200 mb-1">
                Link to Step 8 Entry (optional)
              </label>
              <select
                value={form.step8EntryId || ''}
                onChange={e => handleStep8Selection(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-navy-800 dark:text-navy-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">— Select from Step 8 list or enter manually —</option>
                {step8Entries.map(e => (
                  <option key={e.id} value={e.id}>
                    {e.personName}{e.relationship ? ` (${e.relationship})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-navy-800 dark:text-navy-200 mb-1">
                Person Name <span className="text-danger-500">*</span>
              </label>
              <input
                type="text"
                value={form.personName}
                onChange={e => setForm(prev => ({ ...prev, personName: e.target.value }))}
                placeholder="Who did you make amends to?"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-navy-800 dark:text-navy-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-navy-800 dark:text-navy-200 mb-1">
                Date of Amend
              </label>
              <input
                type="date"
                value={form.dateOfAmend}
                onChange={e => setForm(prev => ({ ...prev, dateOfAmend: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-navy-800 dark:text-navy-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          {/* Amend Type */}
          <div>
            <label className="block text-sm font-medium text-navy-800 dark:text-navy-200 mb-2">
              Type of Amend
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(['direct', 'indirect', 'living', 'written'] as AmendsType[]).map(type => (
                <label
                  key={type}
                  className={`flex flex-col gap-0.5 p-3 rounded-lg border cursor-pointer transition-colors ${
                    form.amendsType === type
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                      : 'border-slate-200 dark:border-navy-600 hover:border-teal-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="amendsType"
                      value={type}
                      checked={form.amendsType === type}
                      onChange={() => setForm(prev => ({ ...prev, amendsType: type }))}
                      className="accent-teal-600"
                    />
                    <span className="text-sm font-medium text-navy-800 dark:text-navy-200">
                      {AMEND_TYPE_LABELS[type]}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 pl-5">
                    {AMEND_TYPE_DESCRIPTIONS[type]}
                  </p>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-navy-800 dark:text-navy-200 mb-1">
              What Was Said / Done <span className="text-danger-500">*</span>
            </label>
            <textarea
              value={form.whatWasSaid}
              onChange={e => setForm(prev => ({ ...prev, whatWasSaid: e.target.value }))}
              placeholder="Describe what you said or did to make this amend..."
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-navy-800 dark:text-navy-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-navy-800 dark:text-navy-200 mb-1">
              Outcome
            </label>
            <textarea
              value={form.outcome}
              onChange={e => setForm(prev => ({ ...prev, outcome: e.target.value }))}
              placeholder="How did it go? How was it received?"
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-navy-800 dark:text-navy-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={handleAdd}
              disabled={saving || !form.personName.trim() || !form.whatWasSaid.trim()}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Record Amend'}
            </button>
            <button
              onClick={() => { setShowAddForm(false); setForm(emptyForm); }}
              className="px-4 py-2 bg-white dark:bg-navy-800 hover:bg-slate-50 dark:hover:bg-navy-700 text-navy-800 dark:text-navy-200 border border-slate-200 dark:border-navy-600 rounded-lg text-sm font-semibold transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Amends Made List */}
      {amendsMade.length === 0 ? (
        <div className="bg-white dark:bg-navy-800 rounded-xl p-8 border border-slate-200 dark:border-navy-700 text-center">
          <p className="text-slate-500 dark:text-slate-400 mb-2">No amends recorded yet.</p>
          <p className="text-sm text-slate-400 dark:text-slate-500">
            {step8Entries.length === 0
              ? 'Complete Step 8 first to build your amends list, then record amends here as you make them.'
              : 'Record amends here as you make them, working through your Step 8 list.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <h4 className="font-semibold text-navy-900 dark:text-navy-100">
            Amends Made ({amendsMade.length})
          </h4>
          {amendsMade.map((amend, index) => {
            const dateStr = amend.dateOfAmend
              ? amend.dateOfAmend.toDate().toLocaleDateString('en-US', {
                  year: 'numeric', month: 'long', day: 'numeric',
                })
              : 'Unknown date';

            return (
              <div
                key={amend.id}
                className="bg-white dark:bg-navy-800 rounded-xl p-4 border border-slate-200 dark:border-navy-700"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-success-100 dark:bg-success-900/30 flex items-center justify-center text-xs font-bold text-success-700 dark:text-success-300">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-navy-900 dark:text-navy-100">
                          {amend.personName}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300">
                          {AMEND_TYPE_LABELS[amend.amendsType]}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {dateStr}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 dark:text-slate-300 mb-1">
                        {amend.whatWasSaid}
                      </p>
                      {amend.outcome && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 italic">
                          Outcome: {amend.outcome}
                        </p>
                      )}
                    </div>
                  </div>
                  {!disabled && (
                    <button
                      onClick={() => handleDelete(amend.id)}
                      className="flex-shrink-0 p-1.5 text-slate-400 hover:text-danger-600 dark:hover:text-danger-400 transition-colors"
                      title="Remove record"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Big Book Guidance */}
      <div className="p-4 bg-serenity-50 dark:bg-serenity-900/10 rounded-lg border border-serenity-200 dark:border-serenity-800">
        <h4 className="font-semibold text-navy-900 dark:text-navy-100 mb-2">Big Book Guidance</h4>
        <p className="text-sm text-slate-700 dark:text-slate-300 italic">
          "Although these reparations take innumerable forms, there are some general principles which
          we find guiding. Reminding ourselves that we have decided to go to any lengths to find a
          spiritual experience, we ask that we be given strength and direction to do the right thing,
          no matter what the personal consequences may be."
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-500 mt-2">— Big Book, pages 79–80</p>
      </div>
    </div>
  );
}
